import React, { useEffect, useState } from 'react';
import { PlusIcon, EllipsisVerticalIcon } from '@heroicons/react/16/solid';
import { Dropdown, DropdownButton, DropdownItem, DropdownMenu } from '../../../components/Dropdown';
import { Badge } from '../../../components/Badge';
import { Link } from '../../../components/Link';
import { Button } from '../../../components/Button';
import { Heading } from '../../../components/Heading';
import { Divider } from '../../../components/Divider';
import { Avatar } from '../../../components/Avatar';
import Loading from '../../../components/Loading';
import Pagination from '../../../components/Pagination'; // Import the Pagination component
import { Card } from '../../../components/Card';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogActions, DialogBody, DialogDescription, DialogTitle } from '../../../components/Dialog';
import { useWarehouses } from '../../../hooks/useWarehouse';
import { ListToolbarRow, ListToolbarSearch, FiltersButton, SortMenu } from '../../../components/ListToolbar';
import { FiltersDialog, FilterPills, FilterPill } from '../../../components/FiltersDialog';
import { DateRangePicker } from '../../../components/DateRangePicker';
import { Select } from '../../../components/Select';
import { Text } from '../../../components/Text';

const SORT_OPTIONS = [
    { label: 'Name (A-Z)', value: 'az' },
    { label: 'Name (Z-A)', value: 'za' },
];

const SetupWarehousesPage: React.FC = () => {
    const { warehouses, loading, error, addWarehouse, deleteWarehouse, fetchWarehouses, totalWarehouse, } = useWarehouses();
    const [searchQuery, setSearchQuery] = useState<string>(''); // State to store the search query
    const [currentPage, setCurrentPage] = useState<number>(1); // State to track the current page
    const [warehousePerPage] = useState<number>(10); // Number of Warehouses per page
    const [isOpen, setIsOpen] = useState(false); // Dialog open state
    const [isDeleting, setIsDeleting] = useState(false); // Loading state for deletion
    const [selectedWarehouseId, setSelectedWarehouseId] = useState<string | null>(null); // ID of the Warehouse to be deleted
    const [sortOrder, setSortOrder] = useState('az');
    const navigate = useNavigate();
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [filterStartDate, setFilterStartDate] = useState('');
    const [filterEndDate, setFilterEndDate] = useState('');
    const [filterYear, setFilterYear] = useState('');
    const [filterType, setFilterType] = useState('');

    const resetFilters = () => {
        setFilterStartDate('');
        setFilterEndDate('');
        setFilterYear('');
        setFilterType('');
    };

    const availableYears = Array.from(
        new Set(
            (Array.isArray(warehouses) ? warehouses : [])
                .map((w) => new Date(w.created_time).getFullYear())
                .filter((year) => !Number.isNaN(year))
        )
    ).sort((a, b) => b - a);

    const activeFilterCount = [filterStartDate, filterEndDate, filterYear, filterType].filter(Boolean).length;

    const filterPills: FilterPill[] = [
        ...(filterStartDate ? [{ key: 'from', label: `From: ${filterStartDate}`, onClear: () => setFilterStartDate('') }] : []),
        ...(filterEndDate ? [{ key: 'to', label: `To: ${filterEndDate}`, onClear: () => setFilterEndDate('') }] : []),
        ...(filterYear ? [{ key: 'year', label: `Year: ${filterYear}`, onClear: () => setFilterYear('') }] : []),
        ...(filterType ? [{ key: 'type', label: `Type: ${filterType === '1' ? 'HQ' : 'Branch'}`, onClear: () => setFilterType('') }] : []),
    ];

    // Ensure warehouses is always an array before filtering
    const filteredWarehouses = Array.isArray(warehouses)
        ? warehouses.filter((warehouse) => {
            const matchesSearch = warehouse.warehouse_name!.toLowerCase().includes(searchQuery.toLowerCase());
            const matchesStartDate = !filterStartDate || new Date(warehouse.created_time) >= new Date(filterStartDate);
            const matchesEndDate = !filterEndDate || new Date(warehouse.created_time) <= new Date(filterEndDate);
            const matchesYear = !filterYear || new Date(warehouse.created_time).getFullYear().toString() === filterYear;
            const matchesType = !filterType || warehouse.hq === filterType;
            return matchesSearch && matchesStartDate && matchesEndDate && matchesYear && matchesType;
        })
        : [];

    // Pagination logic
    const indexOfLastWarehouse = currentPage * warehousePerPage;
    const indexOfFirstWarehouse = indexOfLastWarehouse - warehousePerPage;
    const currentWarehouses = filteredWarehouses.slice(indexOfFirstWarehouse, indexOfLastWarehouse);
    const totalPages = Math.ceil(filteredWarehouses.length / warehousePerPage);

    // Open the delete dialog with the selected Warehouse's ID
    const handleOpenDeleteDialog = (WarehouseId: string) => {
        setSelectedWarehouseId(WarehouseId);
        setIsOpen(true);
    };

    // Handle delete Warehouse
    const handleDelete = async () => {
        if (!selectedWarehouseId) return;

        setIsDeleting(true);
        try {
            await deleteWarehouse(Number(selectedWarehouseId));
            fetchWarehouses(); // Refresh the Warehouse list
            setIsOpen(false);
        } catch (error) {
            console.error('Failed to delete warehouse:', error);
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <>
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div className="max-sm:w-full sm:flex-1">
                    <Heading>Warehouses</Heading>
                </div>
                <Button onClick={() => navigate('/gp/Warehouses/add')}><PlusIcon />Create new warehouse</Button>
            </div>

            <Card className="mt-6 p-5">
            <ListToolbarRow search={<ListToolbarSearch value={searchQuery} onChange={setSearchQuery} placeholder="Search warehouse…" />}>
                <FiltersButton count={activeFilterCount} onClick={() => setIsFilterOpen(true)} />
                <SortMenu options={SORT_OPTIONS} value={sortOrder} onChange={setSortOrder} />
            </ListToolbarRow>
            <FilterPills pills={filterPills} onClearAll={resetFilters} />
            <ul>
                {loading && <div className='w-full text-center h-full py-36 justify-center'><Loading /></div>}
                {error && <p className='py-16 text-center text-red-700'>{error}</p>}
                {!loading && !error && currentWarehouses.length === 0 && <p className='py-16 text-center'>No Warehouses found.</p>}
                {!loading && !error && [...currentWarehouses].sort((a, b) => sortOrder === 'az' ? a.warehouse_name!.localeCompare(b.warehouse_name!) : b.warehouse_name!.localeCompare(a.warehouse_name!)).map((warehouse, index) => (
                    <li key={warehouse.id} className="rounded-xl hover:bg-secondary">
                        <Divider soft={index > 0} />
                        <div className="flex items-center justify-between px-2">
                            <div className="flex gap-6 py-5">
                                <Avatar square initials={warehouse.warehouse_name![0]} className="bg-brand-teal/10 text-brand-teal size-12" />
                                <div className="">
                                    <div className="text-base/6 font-semibold">
                                        <Link href={`/gp/warehouses/view/${warehouse.id}`}>{warehouse.warehouse_name}</Link>
                                    </div>
                                    <div className="text-xs/6 text-zinc-600">
                                        {warehouse.location ? warehouse.location : 'Undefined location'}
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-4">
                                <Dropdown>
                                    <DropdownButton plain aria-label="More options">
                                        <EllipsisVerticalIcon />
                                    </DropdownButton>
                                    <DropdownMenu anchor="bottom end">
                                        <DropdownItem onClick={() => navigate(`/gp/warehouses/view/${warehouse.id}`)}>View</DropdownItem>
                                        <DropdownItem onClick={() => navigate(`/gp/warehouses/edit/${warehouse.id}`)}>Edit</DropdownItem>
                                        <DropdownItem onClick={() => handleOpenDeleteDialog(warehouse.id!.toString())}>Delete</DropdownItem>
                                    </DropdownMenu>
                                </Dropdown>
                            </div>
                        </div>
                    </li>
                ))}
            </ul>

            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                totalResult={totalWarehouse}
                dataName='Warehouses'
            />
            </Card>

            <FiltersDialog open={isFilterOpen} onClose={() => setIsFilterOpen(false)} onReset={resetFilters}>
                <div className="sm:col-span-2">
                    <Text>Date Range</Text>
                    <DateRangePicker
                        from={filterStartDate}
                        to={filterEndDate}
                        onChange={({ from, to }) => { setFilterStartDate(from); setFilterEndDate(to); }}
                        className="w-full"
                    />
                </div>
                <div>
                    <Text>By Year</Text>
                    <Select value={filterYear} onChange={(e) => setFilterYear(e.target.value)}>
                        <option value="">All Years</option>
                        {availableYears.map((year) => (
                            <option key={year} value={year.toString()}>{year}</option>
                        ))}
                    </Select>
                </div>
                <div>
                    <Text>By Type</Text>
                    <Select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
                        <option value="">All Types</option>
                        <option value="1">HQ</option>
                        <option value="0">Branch</option>
                    </Select>
                </div>
            </FiltersDialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={isOpen} onClose={() => setIsOpen(false)}>
                <DialogTitle>Delete Warehouse</DialogTitle>
                <DialogDescription>
                    Are you sure you want to delete this warehouse? This action cannot be undone.
                </DialogDescription>
                <DialogActions>
                    <Button plain onClick={() => setIsOpen(false)} disabled={isDeleting}>
                        Cancel
                    </Button>
                    <Button onClick={handleDelete} disabled={isDeleting}>
                        {isDeleting ? <Loading /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default SetupWarehousesPage;
